import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BellRing, CalendarClock, ClipboardCheck, MessageSquareText } from 'lucide-react';
import { previewCollectionSettings, updateCollectionSettings } from '../../../../api/collections';
import { showToast } from '../../../../utils/showToast';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Modal } from '../../../../components/ui/Modal';
import { SectionCard } from '../../../../components/ui/SectionCard';
import { Select } from '../../../../components/ui/Select';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { Switch } from '../../../../components/ui/Switch';
import DeliveryStatusBanner from '../components/DeliveryStatusBanner';
import WhatsappNumberSelect from '../components/WhatsappNumberSelect';
import ReminderTemplateSelect from '../components/ReminderTemplateSelect';
import ReminderStepsEditor from './ReminderStepsEditor';
import {
  MAX_SEND_HOUR_LOCAL,
  formToSettingsRequest,
  isSettingsFormDirty,
  parseStepRows,
  settingsToForm,
  validateSettingsForm,
} from '../utils/collectionSettingsForm';
import {
  CHANNEL_LABELS,
  REMINDER_CHANNELS,
  apiErrorCode,
  apiErrorStatus,
  formatHour,
  formatLocalDateTime,
  selectedChannels,
} from '../utils/collectionReminders';
import { findTemplate, templateErrorText } from '../utils/reminderTemplates';

const SEND_HOURS = Array.from({ length: MAX_SEND_HOUR_LOCAL + 1 }, (_, h) => h);

// Nombre accesible de cada selector de plantilla por defecto (el rótulo visible es el canal).
const DEFAULT_TEMPLATE_LABELS = {
  email: 'Plantilla por defecto del correo',
  whatsapp: 'Plantilla por defecto de WhatsApp',
};

const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} className="text-xs text-status-error">
      {children}
    </p>
  ) : null;

/**
 * Nombre para mostrar de una plantilla elegida en la configuración. `null` es la opción sin
 * plantilla propia (`noneLabel`, ya traducido). El nombre es texto del operador: no pasa
 * por t().
 */
function templateLabel(catalog, templateId, noneLabel, t) {
  if (!templateId) return noneLabel;
  const template = findTemplate(catalog, templateId);
  if (template) return template.name;
  return catalog ? t('Plantilla archivada o no disponible') : t('Plantilla guardada');
}

// ── Plantillas rechazadas al guardar (400 invalid_template_reference de billing) ────────

// Formatos EXACTOS de los `fields` de billing: la plantilla por defecto de un canal y la de
// un paso, que billing identifica por sus DÍAS de atraso (no por su posición en la lista).
const DEFAULT_TEMPLATE_FIELD = /^defaultTemplateIds\.(email|whatsapp)$/;
const STEP_TEMPLATE_FIELD = /^autoReminders\.stepTemplates\[(\d+)\]\.(email|whatsapp)TemplateId$/;

/**
 * Campos de un 400 `invalid_template_reference` de billing → plantillas rechazadas. Lo que no
 * tenga el formato conocido se ignora (billing puede sumar campos en el futuro).
 *
 * @param {unknown} fields `err.response.data.fields` (puede no ser un arreglo)
 * @returns {Array<{ kind: 'default'|'step', channel: 'email'|'whatsapp', days?: number }>}
 */
function parseInvalidTemplateFields(fields) {
  const list = Array.isArray(fields) ? fields : [];
  const refs = [];
  for (const field of list) {
    if (typeof field !== 'string') continue;
    const asDefault = DEFAULT_TEMPLATE_FIELD.exec(field);
    if (asDefault) {
      refs.push({ kind: 'default', channel: asDefault[1] });
      continue;
    }
    const asStep = STEP_TEMPLATE_FIELD.exec(field);
    if (asStep) refs.push({ kind: 'step', days: Number(asStep[1]), channel: asStep[2] });
  }
  return refs;
}

/**
 * Detalle legible de las plantillas rechazadas, para sumar al texto del error: una parte por
 * referencia, en el ORDEN de `fields` y sin repetir.
 *
 * @param {ReturnType<typeof parseInvalidTemplateFields>} refs
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]} partes ya traducidas
 */
function invalidTemplateDetailParts(refs, t) {
  const parts = [];
  for (const ref of refs) {
    const canal = t(CHANNEL_LABELS[ref.channel]);
    const part =
      ref.kind === 'default'
        ? t('Plantilla por defecto · {{canal}}', { canal })
        : t('Paso de {{dias}} días · {{canal}}', { dias: ref.days, canal });
    if (!parts.includes(part)) parts.push(part);
  }
  return parts;
}

const InlineWarning = ({ children }) => (
  <p className="flex items-start gap-1.5 rounded-md bg-status-warning-soft px-2.5 py-1.5 text-xs text-status-warning">
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
    <span>{children}</span>
  </p>
);

/** Lo que va a pasar al guardar, según billing (POST settings/preview). */
function PreviewSummary({ preview, body, delivery, catalog }) {
  const { t } = useTranslation();
  const { data } = preview;
  const term = body.defaultPaymentTermDays;
  const ar = body.autoReminders;
  const runs = data.companiesNextRun;
  const channels = selectedChannels(ar.channels);
  // `legacyWithTerm`: las que SÍ recibirían fecha (su comercio o el general tienen plazo).
  // Un billing anterior no lo manda: entonces solo se sabe el máximo.
  const legacyTotal = Number(data.legacyWithoutDueDate) || 0;
  const hasWithTerm = typeof data.legacyWithTerm === 'number';
  const legacyWithTerm = hasWithTerm ? data.legacyWithTerm : legacyTotal;
  const legacyStayWithout = hasWithTerm ? Math.max(0, legacyTotal - legacyWithTerm) : 0;

  return (
    <ul className="m-0 list-disc space-y-2 pl-5 text-sm text-fg">
      <li>
        {term
          ? t('Las notas nuevas vencerán {{n}} días después de su emisión.', { n: term })
          : t('Las notas nuevas no tendrán fecha de vencimiento (salvo los comercios con plazo propio).')}
      </li>
      {body.applyToLegacyUnpaid && (
        <li>
          {t(
            hasWithTerm
              ? '{{n}} notas impagas ya emitidas recibirán fecha de vencimiento y {{m}} quedarán vencidas en este momento.'
              : 'Hasta {{n}} notas impagas ya emitidas recibirán fecha de vencimiento y {{m}} quedarán vencidas en este momento.',
            { n: legacyWithTerm, m: data.wouldBecomeOverdue ?? 0 },
          )}
          {legacyStayWithout > 0 && (
            <span className="mt-1 block text-status-warning">
              {t('{{n}} quedarán sin vencimiento: su comercio no tiene plazo propio y no hay plazo general.', {
                n: legacyStayWithout,
              })}
            </span>
          )}
        </li>
      )}
      {runs ? (
        <li>
          {t('Próximo envío automático: {{fecha}} (hora de {{zona}}).', {
            fecha: formatLocalDateTime(data.nextRunAtLocal) || '—',
            zona: data.operatorTimeZone,
          })}
          <ul className="mt-1 list-[circle] space-y-0.5 pl-5">
            {channels.includes('email') && (
              <li>{t('Hasta {{n}} comercios recibirán recordatorio por correo.', { n: runs.email ?? 0 })}</li>
            )}
            {channels.includes('whatsapp') && (
              <li>{t('Hasta {{n}} comercios recibirán recordatorio por WhatsApp.', { n: runs.whatsapp ?? 0 })}</li>
            )}
          </ul>
          <span className="mt-1 block text-xs text-fg-muted">
            {t('Es un máximo: no descuenta los comercios sin contactos ni los que ya recibieron un recordatorio en las últimas 24 h.')}
          </span>
        </li>
      ) : (
        <li>{t('Los recordatorios automáticos quedarán apagados.')}</li>
      )}
      {/* Las plantillas viajan solo si cambiaron (y por defecto, solo los canales cambiados). */}
      {body.defaultTemplateIds && (
        <li>
          {t('Plantilla por defecto')}
          <ul className="mt-1 list-[circle] space-y-0.5 pl-5">
            {REMINDER_CHANNELS.filter((channel) => channel in body.defaultTemplateIds).map((channel) => (
              <li key={channel}>
                {t(CHANNEL_LABELS[channel])}:{' '}
                <span className="font-medium">
                  {templateLabel(catalog, body.defaultTemplateIds[channel], t('Plantilla del sistema'), t)}
                </span>
              </li>
            ))}
          </ul>
        </li>
      )}
      {runs && Array.isArray(ar.stepTemplates) && ar.stepTemplates.length > 0 && (
        <li>
          {t('Plantilla de cada paso')}
          <ul className="mt-1 list-[circle] space-y-0.5 pl-5">
            {ar.stepTemplates.map((step) => (
              <li key={step.days}>
                {t('A los {{n}} días de atraso', { n: step.days })}
                {channels.map((channel) => (
                  <span key={channel}>
                    {' · '}
                    {t(CHANNEL_LABELS[channel])}:{' '}
                    <span className="font-medium">
                      {templateLabel(catalog, step[`${channel}TemplateId`], t('Plantilla por defecto'), t)}
                    </span>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </li>
      )}
      {ar.enabled && delivery && !delivery.schedulerEnabled && (
        <li className="text-status-warning">
          {t('El envío está apagado en este entorno: no saldrá ningún recordatorio hasta que se habilite.')}
        </li>
      )}
    </ul>
  );
}

/**
 * Configuración de la cobranza del operador: plazo general de vencimiento (primero: todo lo
 * demás depende de él) y regla de recordatorios automáticos.
 *
 * Guardar es en dos pasos: "Revisar y guardar" pide a billing el EFECTO de la configuración
 * propuesta (cuántas notas vencen en el acto, cuántos comercios reciben recordatorio y
 * cuándo) y recién al confirmar se guarda. Activar la regla o completar vencimientos viejos
 * puede disparar un envío masivo que el operador tiene que ver venir.
 *
 * Con un billing que ya tiene plantillas, cada paso elige su plantilla por canal y se elige
 * la plantilla por defecto de cada canal (la de los envíos manuales, programados y pasos sin
 * plantilla propia).
 *
 * Guardar recarga también el catálogo de plantillas: billing calcula ahí qué plantilla es la
 * por defecto y dónde se usa cada una, y eso cambia con la configuración.
 *
 * `onDirtyChange` informa si hay cambios sin guardar: el formulario vive en esta pestaña y
 * cambiar de pestaña lo desmonta, así que la pantalla pide confirmar antes de salir.
 *
 * @param {{ setup: ReturnType<typeof import('../hooks/useCollectionsSetup').default>,
 *   templates?: ReturnType<typeof import('../hooks/useReminderTemplates').default>,
 *   onManageTemplates?: () => void, onDirtyChange?: (dirty: boolean) => void }} props
 */
export default function CollectionSettingsTab({ setup, templates, onManageTemplates, onDirtyChange }) {
  const { t } = useTranslation();
  const baseId = useId();
  const {
    view,
    loading,
    error,
    integrations,
    integrationsError,
    integrationsLoading,
    reload,
    reloadIntegrations,
  } = setup;

  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { data, body }
  const [saving, setSaving] = useState(false);

  // Cada carga (inicial o tras guardar) reinicia el formulario con lo guardado.
  useEffect(() => {
    if (!view?.settings) return;
    const next = settingsToForm(view.settings);
    setInitial(next);
    setForm(next);
    setErrors({});
  }, [view]);

  const steps = useMemo(() => (form ? parseStepRows(form.steps) : null), [form]);
  const dirty = form && initial ? isSettingsFormDirty(form, initial) : false;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  // Desmontado, no queda nada sin guardar.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  if (loading && !view) {
    return (
      <div className="space-y-4" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }
  if (error && !view) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>{error.message || t('No se pudo cargar la configuración de cobranza.')}</span>
        <Button size="sm" variant="outline" onClick={reload}>
          {t('Reintentar')}
        </Button>
      </div>
    );
  }
  if (!form) return null;

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setFormError(null);
  };
  // Elegir otra plantilla borra la marca de "rechazada por billing" de ese selector (y solo de
  // ese): la marca describe la plantilla anterior, no la nueva.
  const changeDefaultTemplate = (channel, templateId) => {
    update({ defaultTemplateIds: { ...form.defaultTemplateIds, [channel]: templateId } });
    setErrors((prev) => {
      if (!prev.defaultTemplates?.[channel]) return prev;
      const { [channel]: _cleared, ...rest } = prev.defaultTemplates;
      return { ...prev, defaultTemplates: rest };
    });
  };
  // Los pasos llegan como filas completas: se compara por `key` contra el formulario y se borra
  // la marca de cada canal cuya plantilla cambió. Cambiar solo los días no la toca.
  const changeSteps = (rows) => {
    const changed = rows.flatMap((row) => {
      const prevRow = form.steps.find((r) => r.key === row.key);
      if (!prevRow) return [];
      return REMINDER_CHANNELS.filter(
        (channel) => row[`${channel}TemplateId`] !== prevRow[`${channel}TemplateId`],
      ).map((channel) => [row.key, channel]);
    });
    update({ steps: rows });
    if (changed.length === 0) return;
    setErrors((prev) => {
      if (!prev.stepTemplates) return prev;
      const stepTemplates = { ...prev.stepTemplates };
      let cleared = false;
      for (const [key, channel] of changed) {
        if (!stepTemplates[key]?.[channel]) continue;
        const { [channel]: _cleared, ...rest } = stepTemplates[key];
        stepTemplates[key] = rest;
        cleared = true;
      }
      return cleared ? { ...prev, stepTemplates } : prev;
    });
  };
  const legacyCount = Number(view.legacyUnpaidWithoutDueDate) || 0;
  const timeZone = view.operatorTimeZone;
  const channels = selectedChannels(form.channels);
  const catalog = templates?.catalog ?? null;
  const ids = {
    term: `${baseId}-term`,
    hour: `${baseId}-hour`,
    legacy: `${baseId}-legacy`,
  };
  // La opción "por defecto" de un paso muestra qué plantilla es hoy (la elegida en el
  // formulario, aunque todavía no se haya guardado).
  const stepDefaultLabels = Object.fromEntries(
    REMINDER_CHANNELS.map((channel) => [
      channel,
      `${t('Por defecto')}: ${templateLabel(catalog, form.defaultTemplateIds[channel], t('Plantilla del sistema'), t)}`,
    ]),
  );
  // Canales apagados con plantillas asignadas en algún paso (ahora o en lo guardado): su
  // columna se sigue mostrando para poder quitarlas. Mirar también lo guardado evita que la
  // columna desaparezca apenas se limpia la última, antes de guardar.
  const pinnedChannels = REMINDER_CHANNELS.filter(
    (channel) =>
      !form.channels[channel] &&
      [...form.steps, ...(initial?.steps ?? [])].some((row) => row[`${channel}TemplateId`]),
  );

  // Recarga lo guardado y el catálogo (usedIn y "por defecto" dependen de la configuración).
  const reloadAfterSave = () => Promise.all([reload(), templates?.reload()]);

  /**
   * Marca las plantillas que billing rechazó al guardar: la por defecto de cada canal
   * (`errors.defaultTemplates`) y la de un paso (`errors.stepTemplates[<key>][<canal>]`). La
   * fila se busca por sus DÍAS de atraso, que es como la identifica billing (los pasos van en
   * `form.steps`, no en el body, que solo lleva los días); una que ya no esté en el formulario
   * se ignora. Conserva los errores de las demás filas.
   *
   * Los errores de plantilla van aparte de `errors.stepRows` (que es de los DÍAS de
   * `validateSettingsForm`): el editor marca cada uno en su campo y no se mezclan.
   */
  const markInvalidTemplateReferences = (refs) => {
    const defaultTemplates = {};
    // Fila (key) → canales rechazados de esa fila.
    const rejected = new Map();
    for (const ref of refs) {
      if (ref.kind === 'default') {
        defaultTemplates[ref.channel] = true;
        continue;
      }
      const row = form.steps.find((r) => Number(String(r.days).trim()) === ref.days);
      if (!row) continue;
      rejected.set(row.key, { ...rejected.get(row.key), [ref.channel]: true });
    }
    const hasDefaults = Object.keys(defaultTemplates).length > 0;
    if (!hasDefaults && rejected.size === 0) return;
    setErrors((prev) => ({
      ...prev,
      ...(hasDefaults ? { defaultTemplates } : {}),
      ...(rejected.size > 0
        ? { stepTemplates: { ...prev.stepTemplates, ...Object.fromEntries(rejected) } }
        : {}),
    }));
  };

  const handleReview = async (e) => {
    e.preventDefault();
    const validation = validateSettingsForm(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const body = formToSettingsRequest(form, initial);
    setPreviewing(true);
    setFormError(null);
    try {
      const { data } = await previewCollectionSettings(body);
      setPreview({ data, body });
    } catch (err) {
      setFormError(templateErrorText(err, t, 'No se pudo calcular el efecto de la configuración.'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    const { body } = preview;
    setSaving(true);
    try {
      const { data } = await updateCollectionSettings(body);
      setPreview(null);
      if (data?.backfill) {
        showToast(
          t('Configuración guardada. Se completó el vencimiento de {{n}} notas.', { n: data.backfill.updated ?? 0 }),
          'success',
        );
        if (data.backfill.skippedWithoutTerm > 0) {
          showToast(
            t('{{n}} notas siguen sin vencimiento: su comercio no tiene plazo propio y no hay plazo general.', {
              n: data.backfill.skippedWithoutTerm,
            }),
            'warning',
          );
        }
      } else {
        showToast(t('Configuración de cobranza guardada.'), 'success');
      }
      await reloadAfterSave();
    } catch (err) {
      setPreview(null);
      const status = apiErrorStatus(err);
      // Con el completado pedido, un 500 es el backfill interrumpido: la configuración ya se
      // guardó y repetirlo es seguro (solo toca las notas que siguen sin vencimiento).
      const backfillInterrupted = body.applyToLegacyUnpaid && status === 500;
      // Sin respuesta o 502+: el proxy cortó (30 s) mientras billing seguía completando
      // vencimientos. La configuración se guarda ANTES del completado, así que pudo quedar
      // guardada: se recarga para mostrar lo que hay, y repetir sigue siendo seguro.
      const maybeSaved = body.applyToLegacyUnpaid && (status === null || status >= 502);
      let text;
      if (backfillInterrupted) {
        text = t('La configuración se guardó, pero completar los vencimientos se interrumpió. Volvé a tildarlo y guardá: es seguro repetirlo.');
      } else if (maybeSaved) {
        text = t('No hubo respuesta a tiempo: la configuración pudo haberse guardado y el completado de vencimientos puede seguir en curso. Se recargó lo guardado; si no ves tus cambios, volvé a guardar: es seguro repetirlo.');
      } else {
        text = templateErrorText(err, t, 'No se pudo guardar la configuración de cobranza.');
        // Billing rechazó alguna plantilla elegida (se archivó o es de otro canal): el 400
        // trae en `fields` cuáles. Se marcan esos selectores para elegir otra, y se dice
        // cuál falló: el texto genérico no alcanza para encontrarla en un formulario largo.
        if (apiErrorCode(err) === 'invalid_template_reference') {
          const refs = parseInvalidTemplateFields(err?.response?.data?.fields);
          markInvalidTemplateReferences(refs);
          const parts = invalidTemplateDetailParts(refs, t);
          if (parts.length > 0) {
            text = `${text} ${t('Revisá: {{detalle}}.', { detalle: parts.join('; ') })}`;
          }
        }
      }
      setFormError(text);
      showToast(text, 'error');
      // Una plantilla elegida se archivó mientras tanto: se recarga la lista para que el
      // selector la muestre como no disponible y el operador elija otra.
      if (apiErrorCode(err) === 'invalid_template_reference') templates?.reload();
      if (backfillInterrupted || maybeSaved) await reloadAfterSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form className="space-y-4" onSubmit={handleReview} noValidate>
        <DeliveryStatusBanner delivery={view.delivery} channels={form.enabled ? channels : []} />

        {/* Falló una recarga: se sigue mostrando la última configuración (y lo editado). */}
        {error && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
            <span>{t('No se pudo actualizar la configuración de cobranza. Se muestra la última que se cargó.')}</span>
            <Button size="sm" variant="outline" onClick={reload} disabled={loading}>
              {t('Reintentar')}
            </Button>
          </div>
        )}

        {/* 1) Plazo general: primero, porque sin plazo nada vence y nada se recuerda. */}
        <SectionCard
          tone="bg"
          icon={<CalendarClock className="h-4 w-4" />}
          title={t('Plazo de vencimiento')}
          subtitle={t('Días corridos desde la emisión de cada nota. Un comercio puede tener su propio plazo en "Excepciones por comercio".')}
        >
          <fieldset className="space-y-2">
            <legend className="sr-only">{t('Plazo de vencimiento')}</legend>
            <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
              <input
                type="radio"
                name={`${baseId}-term-mode`}
                className="accent-brand-500"
                checked={!form.hasTerm}
                onChange={() => update({ hasTerm: false })}
              />
              {t('Sin plazo general: las notas no vencen')}
            </label>
            {/* El campo de días va FUERA del label del radio: dentro, un clic en el campo
                activaría el radio y le robaría el foco. */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`${baseId}-term-mode`}
                  className="accent-brand-500"
                  checked={form.hasTerm}
                  onChange={() => update({ hasTerm: true })}
                />
                {t('Vencen a los')}
              </label>
              <Input
                id={ids.term}
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                className="w-24"
                aria-label={t('Días de plazo')}
                value={form.termDays}
                invalid={!!errors.termDays}
                aria-describedby={errors.termDays ? `${ids.term}-error` : undefined}
                onFocus={() => !form.hasTerm && update({ hasTerm: true })}
                onChange={(e) => update({ termDays: e.target.value, hasTerm: true })}
              />
              <span>{t('días de su emisión')}</span>
            </div>
            <FieldError id={`${ids.term}-error`}>{errors.termDays && t(errors.termDays)}</FieldError>
            <p className="text-xs text-fg-muted">
              {t('Cambiarlo solo afecta a las notas que se emitan desde ahora: las ya emitidas conservan su vencimiento.')}
            </p>
          </fieldset>

          {legacyCount > 0 && (
            <div className="mt-4 rounded-md border border-border p-3">
              <label htmlFor={ids.legacy} className="flex items-start gap-2 text-sm text-fg cursor-pointer">
                <input
                  id={ids.legacy}
                  type="checkbox"
                  className="mt-0.5 accent-brand-500"
                  checked={form.applyToLegacyUnpaid}
                  onChange={(e) => update({ applyToLegacyUnpaid: e.target.checked })}
                />
                <span>
                  {t('Completar el vencimiento de las {{n}} notas impagas ya emitidas que no lo tienen', { n: legacyCount })}
                  <span className="mt-0.5 block text-xs text-fg-muted">
                    {t('Se calcula desde la emisión de cada nota con el plazo de su comercio o, si no tiene, el general. Algunas pueden quedar vencidas en el acto: lo vas a ver antes de confirmar.')}
                  </span>
                </span>
              </label>
            </div>
          )}
        </SectionCard>

        {/* 2) Regla automática */}
        <SectionCard
          tone="bg"
          icon={<BellRing className="h-4 w-4" />}
          title={t('Recordatorios automáticos')}
          subtitle={t('A las notas vencidas, en cada paso de días de atraso. Cada comercio recibe un solo mensaje por canal que agrupa sus notas vencidas, y como máximo uno cada 24 h.')}
          action={
            <Switch
              checked={form.enabled}
              onChange={(v) => update({ enabled: v })}
              label={form.enabled ? t('Activos') : t('Apagados')}
              ariaLabel={t('Recordatorios automáticos')}
            />
          }
        >
          <div className="space-y-4">
            {form.enabled && !form.hasTerm && (
              <InlineWarning>
                {t('Los recordatorios están activos pero no hay plazo general: solo vencen (y reciben recordatorio) las notas de comercios con plazo propio.')}
              </InlineWarning>
            )}

            <ReminderStepsEditor
              baseId={baseId}
              rows={form.steps}
              onChange={changeSteps}
              channels={form.channels}
              templatesSupported={form.templatesSupported}
              catalog={catalog}
              defaultLabels={stepDefaultLabels}
              pinnedChannels={pinnedChannels}
              rowErrors={errors.stepRows}
              templateErrors={errors.stepTemplates}
              error={errors.stepsDays}
              hint={
                steps && !steps.error && steps.steps.length > 0
                  ? t('Se envía a los {{dias}} días de vencida. Si una nota ya pasó varios pasos, recibe solo el más alto.', {
                      dias: steps.steps.join(', '),
                    })
                  : t('Hasta 6 pasos.')
              }
            />

            <fieldset className="space-y-1.5">
              <legend className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
                {t('Canales')}
              </legend>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-brand-500"
                    checked={form.channels.email}
                    onChange={(e) => update({ channels: { ...form.channels, email: e.target.checked } })}
                  />
                  {t('Correo')}
                </label>
                <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-brand-500"
                    checked={form.channels.whatsapp}
                    onChange={(e) => update({ channels: { ...form.channels, whatsapp: e.target.checked } })}
                  />
                  {t('WhatsApp')}
                </label>
              </div>
              <FieldError>{errors.channels && t(errors.channels)}</FieldError>
            </fieldset>

            <div className="space-y-1.5">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
                {t('Número de WhatsApp')}
              </span>
              <p className="text-xs text-fg-muted">
                {t('Es el que usan también los envíos programados y el "Enviar recordatorio" manual.')}
              </p>
              <WhatsappNumberSelect
                integrations={integrations}
                loading={loading || integrationsLoading}
                error={integrationsError}
                onRetry={reloadIntegrations}
                value={form.whatsappIntegrationId}
                savedId={initial?.whatsappIntegrationId}
                onChange={(id) => update({ whatsappIntegrationId: id })}
              />
              <FieldError>{errors.whatsappIntegrationId && t(errors.whatsappIntegrationId)}</FieldError>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Select
                  id={ids.hour}
                  label={t('Hora de inicio del envío')}
                  value={String(form.sendHourLocal)}
                  onChange={(e) => update({ sendHourLocal: Number(e.target.value) })}
                  hint={t('Hora de {{zona}}. Los envíos del día cierran a las 20:00.', { zona: timeZone })}
                >
                  {SEND_HOURS.map((h) => (
                    <option key={h} value={String(h)}>
                      {formatHour(h)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex items-center pt-5">
                <Switch
                  checked={form.weekdaysOnly}
                  onChange={(v) => update({ weekdaysOnly: v })}
                  label={t('Solo días hábiles (lunes a viernes)')}
                />
              </div>
            </div>
          </div>
        </SectionCard>

        {/* 3) Plantillas por defecto (solo con un billing que ya las tiene) */}
        {form.templatesSupported && (
          <SectionCard
            tone="bg"
            icon={<MessageSquareText className="h-4 w-4" />}
            title={t('Plantillas por defecto')}
            subtitle={t('Se usan en los envíos manuales, en los programados y en los pasos sin plantilla propia.')}
            action={
              onManageTemplates && (
                <Button size="sm" variant="outline" onClick={onManageTemplates}>
                  {t('Administrar plantillas')}
                </Button>
              )
            }
          >
            <div className="space-y-3">
              {templates?.error && !catalog && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
                  <span>{t('No se pudieron cargar las plantillas: se conserva lo guardado.')}</span>
                  <Button size="sm" variant="outline" onClick={templates.reload} disabled={templates.loading}>
                    {t('Reintentar')}
                  </Button>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                {REMINDER_CHANNELS.map((channel) => (
                  <ReminderTemplateSelect
                    key={channel}
                    id={`${baseId}-default-${channel}`}
                    label={t(CHANNEL_LABELS[channel])}
                    ariaLabel={t(DEFAULT_TEMPLATE_LABELS[channel])}
                    channel={channel}
                    value={form.defaultTemplateIds[channel]}
                    onChange={(templateId) => changeDefaultTemplate(channel, templateId)}
                    catalog={catalog}
                    nullLabel={t('Plantilla del sistema')}
                    error={
                      errors.defaultTemplates?.[channel]
                        ? t('Esta plantilla ya no está disponible: elegí otra.')
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          </SectionCard>
        )}

        {formError && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {formError}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            disabled={!dirty || previewing}
            onClick={() => {
              setForm(initial);
              setErrors({});
              setFormError(null);
            }}
          >
            {t('Descartar cambios')}
          </Button>
          <Button type="submit" loading={previewing} disabled={!dirty} iconLeft={<ClipboardCheck className="h-4 w-4" />}>
            {t('Revisar y guardar')}
          </Button>
        </div>
      </form>

      <Modal
        open={!!preview}
        onClose={() => !saving && setPreview(null)}
        size="md"
        title={t('Confirmar configuración de cobranza')}
        description={t('Esto es lo que va a pasar al guardar.')}
        footer={
          <>
            <Button variant="outline" onClick={() => setPreview(null)} disabled={saving}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleConfirm} loading={saving}>
              {t('Confirmar y guardar')}
            </Button>
          </>
        }
      >
        {preview && (
          <PreviewSummary preview={preview} body={preview.body} delivery={view.delivery} catalog={catalog} />
        )}
      </Modal>
    </>
  );
}
